// C:\ViYash\XPRSuvey\XPRSuvey-App\src\Components\Auth\CompanyProtectedRoute.jsx

import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Cookies from "js-cookie";
import { VerifyCompanyUserAPI } from "./Services/AuthService";
import { handleAuthFailure } from "../../Utility/authSession.js";

export default function CompanyProtectedRoute({ children }) {
    const navigate = useNavigate();
    const location = useLocation();

    const [isVerifying, setIsVerifying] = useState(true);
    const [isAuthorized, setIsAuthorized] = useState(false);

    const lastActivityTime = useRef(0);

    useEffect(() => {
        let isMounted = true;

        const verify = async () => {
            const token = Cookies.get("JWTToken");

            if (!token) {
                navigate("/CompanyLogin", { replace: true });
                return;
            }

            try {
                const payload = JSON.parse(atob(token.split(".")[1]));
                const expiryDate = new Date(payload.exp * 1000);

                if (expiryDate <= new Date()) {
                    handleAuthFailure("Your session has expired. Please log in again.", location.pathname, "/CompanyLogin");
                    return;
                }
            } catch (error) {
                handleAuthFailure("Invalid authentication token. Please log in again.", location.pathname, "/CompanyLogin");
                return;
            }

            try {
                const result = await VerifyCompanyUserAPI();

                if (!isMounted) return;

                if (result?.status?.toLowerCase() === "success") {
                    setIsAuthorized(true);
                } else {
                    handleAuthFailure(result?.message || "Session is no longer valid. Please log in again.", location.pathname, "/CompanyLogin");
                }
            } catch (error) {
                if (!isMounted) return;

                handleAuthFailure(error?.response?.data?.message || "Session verification failed. Please log in again.", location.pathname, "/CompanyLogin");
            } finally {
                if (isMounted) {
                    setIsVerifying(false);
                }
            }
        };

        verify();

        return () => {
            isMounted = false;
        };
    }, [navigate, location.pathname]);

    useEffect(() => {
        if (!isAuthorized) return;
        const markActive = () => {
            // Only mark activity when the page is visible.
            if (document.visibilityState !== "visible") {
                return;
            }

            lastActivityTime.current = Date.now();
        };
        window.addEventListener("mousemove", markActive);
        window.addEventListener("mousedown", markActive);
        window.addEventListener("keydown", markActive);
        window.addEventListener("click", markActive);
        window.addEventListener("scroll", markActive);
        window.addEventListener("touchstart", markActive);

        const checkExpiryInterval = setInterval(async () => {
            const token = Cookies.get("JWTToken");

            if (!token) {
                clearInterval(checkExpiryInterval);
                handleAuthFailure("Your session has expired. Please log in again.", location.pathname, "/CompanyLogin");
                return;
            }

            if (document.visibilityState !== "visible") {
                return;
            }
            if (!document.hasFocus()) {
                return;
            }
            let expiryDate;
            try {
                const payload = JSON.parse(atob(token.split(".")[1]));

                if (!payload?.exp) {
                    throw new Error("JWT expiry claim is missing.");
                }

                expiryDate = new Date(payload.exp * 1000);
            } catch (error) {
                handleAuthFailure("Invalid authentication token. Please log in again.", location.pathname, "/CompanyLogin");
                return;
            }

            const now = new Date();
            const timeDiffMinutes = (expiryDate.getTime() - now.getTime()) / 1000 / 60;
            const hasUserActivity = lastActivityTime.current > 0;
            const isRecentlyActive = hasUserActivity && Date.now() - lastActivityTime.current < 5 * 60 * 1000;
            if (
                timeDiffMinutes > 0 &&
                timeDiffMinutes <= 5 &&
                isRecentlyActive
            ) {
                try {
                    const result = await VerifyCompanyUserAPI();

                    if (
                        result?.status?.toLowerCase() === "success" &&
                        result?.isRefreshed &&
                        result?.jwtToken
                    ) {
                        Cookies.set("JWTToken", result.jwtToken, { expires: 1, secure: false, sameSite: "Lax" });

                        if (result.tokenExpiry) {
                            Cookies.set("tokenExpiry", result.tokenExpiry, { expires: 1, secure: false, sameSite: "Lax" });
                        }
                        lastActivityTime.current = Date.now();

                    } else if (
                        result?.status?.toLowerCase() !== "success"
                    ) {
                        CustomToast("fail", "Token verification failed:");
                    }
                } catch (error) {
                    CustomToast("fail", "Token refresh failed:");
                }
            }

            else if (timeDiffMinutes <= 0) {
                handleAuthFailure("Your session has expired. Please log in again.", location.pathname, "/CompanyLogin");
            }
        }, 60000);

        return () => {
            window.removeEventListener("mousemove", markActive);
            window.removeEventListener("mousedown", markActive);
            window.removeEventListener("keydown", markActive);
            window.removeEventListener("click", markActive);
            window.removeEventListener("scroll", markActive);
            window.removeEventListener("touchstart", markActive);

            clearInterval(checkExpiryInterval);
        };
    }, [isAuthorized, location.pathname]);

    if (isVerifying || !isAuthorized) {
        return null;
    }

    return children;
}